import { useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';

import type { RecordBody, WorkflowTransitionBody } from '@/shared/api/generated/model';
import { recordsStage } from '@/shared/api/generated/records/records';
import { ApiError } from '@/shared/api/http';
import { Button } from '@/shared/ui/Button';
import { Dialog, DialogContent } from '@/shared/ui/Dialog';
import { Field } from '@/shared/ui/Field';
import { Textarea } from '@/shared/ui/Textarea';
import { toast } from '@/shared/ui/Toast';

import styles from './Stages.module.scss';

/**
 * Перевести запись по переходу: с комментарием — через окно, без — сразу.
 * Возвращает функцию запуска и само окно: им пользуются и кнопки записи,
 * и канбан (перетаскивание карточки).
 */
export function useStageChange(onChanged: (record: RecordBody) => void) {
  const queryClient = useQueryClient();
  const [pending, setPending] = useState<{
    recordId: string;
    transition: WorkflowTransitionBody;
  }>();
  const [comment, setComment] = useState('');
  const [busy, setBusy] = useState(false);

  const send = (recordId: string, transition: WorkflowTransitionBody, text: string) => {
    setBusy(true);

    recordsStage(recordId, {
      to: transition.to,
      ...(text.trim() === '' ? {} : { comment: text.trim() }),
    })
      .then((response) => {
        toast.success(transition.label);
        setPending(undefined);
        setComment('');
        void queryClient.invalidateQueries({ queryKey: ['records'] });
        void queryClient.invalidateQueries({ queryKey: ['record-stages', recordId] });
        onChanged(response.data as RecordBody);
      })
      .catch((cause: unknown) => {
        toast.error(cause instanceof ApiError ? cause.detail : 'Не удалось сменить статус');
      })
      .finally(() => {
        setBusy(false);
      });
  };

  const run = (recordId: string, transition: WorkflowTransitionBody) => {
    if (transition.comment === 'optional' || transition.comment === 'required') {
      setPending({ recordId, transition });

      return;
    }

    send(recordId, transition, '');
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();

    if (pending !== undefined) {
      send(pending.recordId, pending.transition, comment);
    }
  };

  const dialog = (
    <Dialog
      open={pending !== undefined}
      onOpenChange={(next) => {
        if (!next) {
          setPending(undefined);
        }
      }}
    >
      {pending !== undefined && (
        <DialogContent
          title={pending.transition.label}
          description={
            pending.transition.comment === 'required'
              ? 'Комментарий обязателен: его увидят в истории записи, а посетитель — у своей записи на сайте.'
              : 'Комментарий по желанию — останется в истории записи.'
          }
          size="sm"
        >
          <form className={styles['form']} onSubmit={submit}>
            <Field label="Комментарий" required={pending.transition.comment === 'required'}>
              <Textarea
                autoFocus
                rows={3}
                value={comment}
                onChange={(event) => {
                  setComment(event.target.value);
                }}
              />
            </Field>
            <Button
              type="submit"
              loading={busy}
              disabled={pending.transition.comment === 'required' && comment.trim() === ''}
            >
              {pending.transition.label}
            </Button>
          </form>
        </DialogContent>
      )}
    </Dialog>
  );

  return { run, dialog, busy };
}
